import { Box, Button, Flex, FormLabel, Input, Stack, Text, Textarea, useColorModeValue, useDisclosure, useToast } from "@chakra-ui/react";
import FormModal from "../../../components/FormModal";
import { useEffect, useState } from "react";
import PageCard from "../../../components/page-card";
import { translate } from "../../../i18n";
import { typography } from 'theme/tokens';
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import { useUpdateOrganizationCancellationMessages } from "../../organization/hooks/useUpdateOrganizationCancellationMessages";
import { useUpdateOrganizationRefundDeadline } from "../../organization/hooks/useUpdateOrganizationRefundDeadline";

// Soft cap mirrors the API's @MaxLength(500). The textarea also enforces this
// client-side so admins see the counter turn red before the request is rejected.
const CANCELLATION_MESSAGE_MAX_LENGTH = 500;

export default function RefundDeadlinePolicyCard() {
    const { selectedOrganization } = useSelectedOrganization();
    const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
    const toast = useToast();

    const { isOpen, onOpen, onClose } = useDisclosure();
    const messagesModal = useDisclosure();
    const [refundDeadlineMinutes, setRefundDeadlineMinutes] = useState(
        Math.floor((selectedOrganization?.refundDeadline || 0) / 60000) // ms to min
    );

    // Draft state for the two-textarea modal. Initial values are seeded from the
    // selected org and re-seeded each time the modal opens (handleOpenMessages),
    // so canceling-then-reopening reverts to the saved value rather than the
    // last unsaved draft.
    const [withinDraft, setWithinDraft] = useState(
        selectedOrganization?.cancellationMessageWithinDeadline ?? '',
    );
    const [outsideDraft, setOutsideDraft] = useState(
        selectedOrganization?.cancellationMessageOutsideDeadline ?? '',
    );

    // Re-sync drafts when the selected org changes (org switch in the sidebar).
    // Without this, switching tenants would keep the previous tenant's drafts in
    // the textareas.
    useEffect(() => {
        setWithinDraft(selectedOrganization?.cancellationMessageWithinDeadline ?? '');
        setOutsideDraft(selectedOrganization?.cancellationMessageOutsideDeadline ?? '');
    }, [selectedOrganization?.id, selectedOrganization?.cancellationMessageWithinDeadline, selectedOrganization?.cancellationMessageOutsideDeadline]);

    const { mutateAsync: updateOrganizationRefundDeadline, isLoading: isUpdatingRefundDeadline } =
        useUpdateOrganizationRefundDeadline(selectedOrganization?.id || '', onClose);

    const { mutateAsync: updateCancellationMessages, isLoading: isUpdatingMessages } =
        useUpdateOrganizationCancellationMessages(selectedOrganization?.id || '', messagesModal.onClose);

    const handleUpdateRefundDeadline = async () => {
        try {
            const refundDeadlineMilliseconds = isNaN(refundDeadlineMinutes) ? 0 : refundDeadlineMinutes * 60000;
            await updateOrganizationRefundDeadline({ refundDeadline: refundDeadlineMilliseconds });
        } catch {
            toast({ title: translate('error'), status: 'error', duration: 3000, position: 'top-right' });
        }
    };

    // Empty-string drafts are sent as null so the API clears back to the
    // translated default. Trim guards against owners saving whitespace-only
    // strings, which would silently blank out the modal in mobile.
    const handleUpdateCancellationMessages = async () => {
        try {
            await updateCancellationMessages({
                cancellationMessageWithinDeadline: withinDraft.trim() ? withinDraft : null,
                cancellationMessageOutsideDeadline: outsideDraft.trim() ? outsideDraft : null,
            });
        } catch {
            toast({ title: translate('error'), status: 'error', duration: 3000, position: 'top-right' });
        }
    };

    const handleOpenMessages = () => {
        setWithinDraft(selectedOrganization?.cancellationMessageWithinDeadline ?? '');
        setOutsideDraft(selectedOrganization?.cancellationMessageOutsideDeadline ?? '');
        messagesModal.onOpen();
    };

    if (!selectedOrganization) return null;

    const currentHours = Math.floor((selectedOrganization.refundDeadline || 0) / 3600000);
    const hasCustomMessages = Boolean(
        selectedOrganization.cancellationMessageWithinDeadline ||
        selectedOrganization.cancellationMessageOutsideDeadline,
    );

    return (
        <>
            <PageCard title={translate('organization.refund_time_limit')}>
                <Box p={4}>
                    <Box mb={4} color={textColorPrimary}>
                        <Box mb={2}>
                            <strong>{translate('organization.refund_time_limit')}: </strong>
                            {currentHours} {translate('hours')}
                        </Box>
                    </Box>
                    <Button
                        mt={3}
                        size="md"
                        onClick={() => {
                            onOpen();
                            setRefundDeadlineMinutes(Math.floor((selectedOrganization?.refundDeadline || 0) / 60000));
                        }}
                        colorScheme="brand"
                    >
                        {translate('organization.set_refund_time_limit')}
                    </Button>
                </Box>
            </PageCard>

            <PageCard title={translate('organization.cancellation_messages')}>
                <Box p={4}>
                    <Text fontSize={14} mb={4} color={textColorPrimary}>
                        {translate('organization.cancellation_messages_description')}
                    </Text>
                    <Box mb={2} color={textColorPrimary}>
                        <strong>{translate('organization.cancellation_message_within')}: </strong>
                        {selectedOrganization.cancellationMessageWithinDeadline || (
                            <Text as="span" color="gray.500" fontStyle="italic">
                                {translate('organization.cancellation_message_within_placeholder')}
                            </Text>
                        )}
                    </Box>
                    <Box mb={2} color={textColorPrimary}>
                        <strong>{translate('organization.cancellation_message_outside')}: </strong>
                        {selectedOrganization.cancellationMessageOutsideDeadline || (
                            <Text as="span" color="gray.500" fontStyle="italic">
                                {translate('organization.cancellation_message_outside_placeholder')}
                            </Text>
                        )}
                    </Box>
                    <Button
                        mt={3}
                        size="md"
                        onClick={handleOpenMessages}
                        colorScheme="brand"
                        variant={hasCustomMessages ? 'outline' : 'solid'}
                    >
                        {translate('organization.set_cancellation_messages')}
                    </Button>
                </Box>
            </PageCard>

            <FormModal
                isOpen={isOpen}
                onClose={onClose}
                title={translate('organization.set_refund_time_limit')}
                onSubmit={handleUpdateRefundDeadline}
                submitLabel={translate('save')}
                cancelLabel={translate('cancel')}
                isSubmitting={isUpdatingRefundDeadline}
            >
                <Text fontSize={14}>
                    {translate('organization.set_refund_time_limit_description')}
                </Text>
                <Flex direction="row" justify="center" align="center" py={4}>
                    <Input w={20} type='number' value={Math.floor(refundDeadlineMinutes / 60)} onChange={newValue => setRefundDeadlineMinutes(Number.parseInt(newValue.target.value) * 60)} />
                    <FormLabel {...typography.formLabel} mt={2} ml={2}>{translate('hours')}</FormLabel>
                </Flex>
            </FormModal>

            <FormModal
                isOpen={messagesModal.isOpen}
                onClose={messagesModal.onClose}
                title={translate('organization.set_cancellation_messages')}
                onSubmit={handleUpdateCancellationMessages}
                submitLabel={translate('save')}
                cancelLabel={translate('cancel')}
                isSubmitting={isUpdatingMessages}
            >
                <Text fontSize={14} mb={4}>
                    {translate('organization.cancellation_messages_description')}
                </Text>
                <Stack spacing={4}>
                    <Box>
                        <FormLabel {...typography.formLabel}>
                            {translate('organization.cancellation_message_within')}
                        </FormLabel>
                        <Textarea
                            value={withinDraft}
                            onChange={(e) => setWithinDraft(e.target.value.slice(0, CANCELLATION_MESSAGE_MAX_LENGTH))}
                            placeholder={translate('organization.cancellation_message_within_placeholder')}
                            rows={3}
                            maxLength={CANCELLATION_MESSAGE_MAX_LENGTH}
                        />
                        <Text fontSize="xs" color={withinDraft.length >= CANCELLATION_MESSAGE_MAX_LENGTH ? 'red.500' : 'gray.500'} textAlign="right" mt={1}>
                            {withinDraft.length}/{CANCELLATION_MESSAGE_MAX_LENGTH}
                        </Text>
                    </Box>
                    <Box>
                        <FormLabel {...typography.formLabel}>
                            {translate('organization.cancellation_message_outside')}
                        </FormLabel>
                        <Textarea
                            value={outsideDraft}
                            onChange={(e) => setOutsideDraft(e.target.value.slice(0, CANCELLATION_MESSAGE_MAX_LENGTH))}
                            placeholder={translate('organization.cancellation_message_outside_placeholder')}
                            rows={4}
                            maxLength={CANCELLATION_MESSAGE_MAX_LENGTH}
                        />
                        <Text fontSize="xs" color={outsideDraft.length >= CANCELLATION_MESSAGE_MAX_LENGTH ? 'red.500' : 'gray.500'} textAlign="right" mt={1}>
                            {outsideDraft.length}/{CANCELLATION_MESSAGE_MAX_LENGTH}
                        </Text>
                    </Box>
                </Stack>
            </FormModal>
        </>
    );
}
